import React, { useState, useEffect } from 'react';
import styles from './NotificationPage.module.css';
import NavBar from "../../components/NavBar/NavBar";
import { Link } from 'react-router-dom';

// Helper function to simulate a timestamp for visual consistency
const getSimulatedTime = () => {
  const d = new Date();
  return d.toLocaleString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    month: "short",
    day: "numeric"
  });
};

// Function to parse the notification message and extract link data
const parseNotificationData = (message) => {
  // Expected format: "Human readable message | post_id=X | comment_id=Y | comment_content=Z"
  const parts = message.split('|');
  const humanMessage = parts[0].trim();

  let postId = null;
  let commentId = null;
  let commentContent = null; 

  // Parse the context data (post_id, comment_id, and comment_content)
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i].trim();
    if (part.startsWith('post_id=')) {
      postId = part.split('=')[1].trim();
    } else if (part.startsWith('comment_id=')) {
      commentId = part.split('=')[1].trim();
    } else if (part.startsWith('comment_content=')) { 
      let rawEncodedContent = part.split('=').slice(1).join('=').trim();
      let correctedEncodedContent = rawEncodedContent.replace(/\+/g, '%20');
      commentContent = decodeURIComponent(correctedEncodedContent);
    }
  }

  // Construct the target URL for the post/comment
  let targetUrl = null;
  if (postId) {
    targetUrl = `/houseListing/${postId}`;
    if (commentId) {
      targetUrl += `?commentId=${commentId}`;
    }
  }

  // --- Robust Sender/Action Split Logic ---
  const colonIndex = humanMessage.indexOf(':');
  let sender;
  let rawActionText;

  if (colonIndex > 0) {
    sender = humanMessage.substring(0, colonIndex).trim();
    rawActionText = humanMessage.substring(colonIndex + 1).trim();
  } else {
    sender = "Someone"; 
    rawActionText = humanMessage;
  }
  
  const lowerAction = rawActionText.toLowerCase();
  let topRowContent; 
  let bottomRowContent; 

  if (lowerAction.includes("commented on your post")) {
    topRowContent = `${sender} commented on your post.`;
    bottomRowContent = commentContent || `${sender} commented on your post.`;
  } else if (lowerAction.includes("replied to your comment")) {
    topRowContent = `${sender} replied to your comment.`;
    bottomRowContent = commentContent || `${sender} replied to your comment.`;
  } else {
    topRowContent = "System Update"; 
    bottomRowContent = rawActionText;
  }

  return {
    targetUrl,
    sender: topRowContent,    
    preview: bottomRowContent, 
  };
};


const NotificationList = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 1. New State for Error Messages (Matches RegisterPage.jsx)
  const [msg, setMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    const fetchNotifications = async () => {
      try { 
        const response = await fetch('https://weatherbuddy.org/HomeMate/backend/get_notifications.php', { //update this to a base_url variable
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json(); 

        const formatted = data.map((note) => {
          const fullMessage = note.message || ""; 
          const parsedData = parseNotificationData(fullMessage);

          return {
            id: note.id, 
            message: fullMessage,
            ...parsedData, 
            is_unread: false, 
            time: getSimulatedTime(),
            type: "listing", 
          };
        });

        setNotifications(formatted);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
        // 2. Trigger Error Message on Fetch Fail
        setMsg({ type: "error", text: "Invalid session. Please login again" });
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, []);

  // 3. Effect hook to dismiss messages after 5 seconds (Matches RegisterPage.jsx)
  useEffect(() => {
      if (msg.text && msg.type === "error") {
          const timer = setTimeout(() => {
              setMsg({ type: "", text: "" }); 
          }, 5000); 
          return () => clearTimeout(timer);
      }
  }, [msg]);

  const dismissNotification = async (id) => {
    setNotifications((prev) => prev.filter(note => note.id !== id));

    try {
      const response = await fetch('https://aptitude.cse.buffalo.edu/CSE442/2025-Fall/cse-442d/backend/dismiss_notification.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_id: id }), 
      });

      if (!response.ok) {
        // 4. Trigger Error Message on Dismiss Fail
        setMsg({ type: "error", text: "Failed to sync with server. Refresh page." });
        console.error(`Failed to dismiss notification ID ${id}.`);
      }
    } catch (error) {
       // 5. Trigger Error Message on Network Error
      setMsg({ type: "error", text: "Network error. Please check your connection." });
      console.error("Network error while dismissing notification:", error);
    }
  };


  return (
    <div className={styles.page}> 
      <div className={styles.fixedHeaderBar} /> 
      <NavBar /> 

      <div className={styles.pageContainer}> 
        <div className={styles.notificationContainer}>

          <div className={styles.header}>
            Notifications ({notifications.length})
          </div>

          {/* 6. Render the Alert Box (Matches RegisterPage.jsx) */}
          {msg.text && (
              <div
                  className={
                      msg.type === "success"
                          ? styles.alertSuccess
                          : styles.alertError
                  }
                  role="status"
              >
                  <p>{msg.text}</p>
              </div>
          )}

          <div className={styles.contentHolder}>
            <ul className={styles.notificationList}>
              {loading ? (
                <li className={styles.noNotifications}>Fetching data...</li>
              ) : notifications.length > 0 ? (
                /* 1. SHOW NOTIFICATIONS IF THEY EXIST */
                notifications.map(note => (
                  <Link 
                    key={note.id} 
                    to={note.targetUrl || '#'} 
                    className={styles.notificationLink} 
                    onClick={(e) => {
                      if (note.targetUrl && note.targetUrl !== '#') {
                        dismissNotification(note.id);
                      } else {
                         e.preventDefault(); 
                      }
                    }}
                  >
                    <li className={styles.notificationItem}>
                      <div className={styles.topRow}>
                        <div className={styles.leftTop}>
                          <span className={styles.notificationSender}>{note.sender}</span>
                        </div>

                        <button
                          className={styles.dismissButton}
                          onClick={(e) => {
                            e.preventDefault(); 
                            e.stopPropagation(); 
                            dismissNotification(note.id);
                          }}
                          aria-label="Dismiss notification"
                        >
                          &times;
                        </button>
                      </div>

                      <div>
                        <p className={styles.notificationMessage}>{note.preview}</p>
                      </div>
                    </li>
                  </Link>
                ))
              ) : msg.type === "error" ? (
                /* 2. IF EMPTY + ERROR: RENDER NOTHING (HIDE 'CAUGHT UP') */
                null
              ) : (
                /* 3. IF EMPTY + NO ERROR: SHOW 'CAUGHT UP' */
                <li className={styles.noNotifications}>🎉 You're all caught up!</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationList;